import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '../../features/auth/context/AuthContext';
import { 
  HomeIcon, 
  WalletIcon, 
  CurrencyDollarIcon, 
  DocumentTextIcon,
  UserIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { FaQuestionCircle } from 'react-icons/fa';
import WelcomeTourModal from '../tour/WelcomeTourModal';
import { useWelcomeTour } from '../tour/useWelcomeTour';

/**
 * Mobile Layout Component - SIMPLIFIED & BULLETPROOF
 * Provides a mobile-friendly layout with bottom navigation
 */
const MobileLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuthContext();
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Welcome tour management
  const { isTourOpen, openTour, closeTour, completeTour } = useWelcomeTour(isAuthenticated);
  
  // Navigation items
  const navItems = [
    { path: '/dashboard', icon: <HomeIcon className="w-6 h-6" />, label: 'Home' },
    { path: '/wallet', icon: <WalletIcon className="w-6 h-6" />, label: 'Wallet' },
    { path: '/cashflow', icon: <CurrencyDollarIcon className="w-6 h-6" />, label: 'Cash' },
    { path: '/transactions', icon: <DocumentTextIcon className="w-6 h-6" />, label: 'Trans' }
  ];

  // Detect current path for active tab styling
  const isActive = (path) => location.pathname.startsWith(path);
  
  const handleNavigation = (path) => {
    navigate(path);
    setShowUserMenu(false);
  };

  return (
    <div className="mobile-layout-container">
      {/* Header */}
      <header className="mobile-header">
        <Link to="/dashboard" className="logo-link">
          <h1 className="logo-text">MnyMny</h1>
        </Link>
        
        <div className="user-menu-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={openTour}
            className="user-menu-button"
            style={{ padding: '8px', minWidth: 'auto' }}
            title="Take a tour"
            aria-label="Open welcome tour"
          >
            <FaQuestionCircle style={{ width: '20px', height: '20px', color: '#8B5CF6' }} />
          </button>
          <button 
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="user-menu-button"
          >
            <span className="user-name">{user?.firstName || 'User'}</span>
            <UserIcon className="user-icon" />
          </button>
        </div>
      </header>

      {/* User Menu Dropdown */}
      {showUserMenu && (
        <div className="user-menu-dropdown">
          <button
            onClick={() => handleNavigation('/profile')}
            className="menu-item"
          >
            <UserIcon className="menu-icon" />
            <span>Profile</span>
          </button>
          <button
            onClick={logout}
            className="menu-item"
          >
            <ArrowRightOnRectangleIcon className="menu-icon" />
            <span>Sign out</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="mobile-main-content">
        <div className="content-wrapper">
          {children}
        </div>
      </main>

      {/* Bottom Navigation - SIMPLIFIED */}
      <nav className="mobile-bottom-nav">
        <div className="nav-container">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={`nav-button ${isActive(item.path) ? 'nav-button-active' : 'nav-button-inactive'}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
      
      {/* Welcome Tour Modal */}
      <WelcomeTourModal
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </div>
  );
};

export default MobileLayout;